import React, { useContext, useState, useEffect, memo, useCallback } from "react";
import { connect } from "react-redux";
import {
  ArrowDownMedium,
  ArrowLeft,
  ArrowRight,
  CalendarIcon,
  Cross,
  DayActiveIcon,
  DayIcon,
  FilterIconSvg,
  MonthActiveIcon,
  MonthIcon,
  RegularArrowDownIcon,
  SearchIcon,
  WeekActiveIcon,
  WeekIcon,
} from "../../../../modules/default-svg";
import ArrowForward from "../../../../assets/svg/arrow_forward.svg";
import Button from "../../../../components/controls/button";
import { projects } from "../../../../store/selectors/projects.selector";
import { getProjects } from "../../../../store/actions/projects.actions";
import CustomPopover from "../../../../components/popover/Popover";
import styles from "../ResourceMonitor.module.scss";
import IconInput from "../../../../components/controls/input/iconInput/IconInput";
import CustomInput from "../../../../components/controls/input/customInput/CustomInput";
import { ResourcePlannerContext } from "../ResourcePlannerContext";
import CheckboxInputStateSave from "../../../../components/controls/checkboxStateSave/CheckboxStateSave";
import Spin from "../../../../components/controls/spinner/Spin";
import { FileIcon } from "lucide-react";

const ResourceHeader = memo(({
  handleCalendarsView,
  buttonStatus,
  allProjects,
  getProjectsRequest,
  dashboard,
  clickHandler,
}) => {
  const {
    project,
    setProject,
    setFilterStartDate,
    tabView,
    setFilterEndDate,
    filterStartDate,
    filterCount,
    openPopover,
    setIsOpenPopover,
    setFilterUsers,
    shifts,
    setFilterShifts,
    setFilterCapacity,
    currentPeriod,
    goToPreviousPeriod,
    goToNextPeriod,
    goToToday,
    // Project search from context
    projectsList,
    projectsLoading,
    searchProjects,
  } = useContext(ResourcePlannerContext);

  const [filterValue, setFilterValue] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedShift, setSelectedShifts] = useState([]);
  const [selectedCapacity, setSelectedCapacity] = useState([]);
  const [resource, setResource] = useState(true);
  const [selection, setSelection] = useState({
    icon: <MonthIcon />,
    name: "Month",
  });

  useEffect(() => {
    if (filterStartDate) {
      setSelection({
        icon: <DayIcon />,
        name: "Day",
      });
    }
  }, [filterStartDate]);

  useEffect(() => {
    if (tabView) {
      if (tabView === "month") {
        setSelection({ icon: <MonthIcon />, name: "Month" });
      } else if (tabView === "week") {
        setSelection({ icon: <WeekIcon />, name: "Week" });
      } else if (tabView === "day") {
        setSelection({ icon: <DayIcon />, name: "Day" });
      } else {
        setSelection({ icon: <MonthIcon />, name: "Month" });
      }
    }
  }, [tabView]);

  const handleChange = useCallback((value) => {
    setFilterValue(value);
    searchProjects(value);
  }, [searchProjects]);

  const handleSelectShift = useCallback((id) => {
    setSelectedShifts((prevShifts) => {
      if (prevShifts.includes(id)) {
        return prevShifts.filter((shiftId) => shiftId !== id);
      } else {
        return [...prevShifts, id];
      }
    });
  }, []);

  const handleSelectCapacity = useCallback((id) => {
    setSelectedCapacity((prevCapacity) => {
      if (prevCapacity.includes(id)) {
        return prevCapacity.filter((capId) => capId !== id);
      } else {
        return [...prevCapacity, id];
      }
    });
  }, []);

  const handleTodayClick = useCallback(() => {
    goToToday();
  }, [goToToday]);

  const handleClearFilters = useCallback(() => {
    setFilterStartDate(null);
    setFilterEndDate(null);
    setStartDate(null);
    setEndDate(null);
    setIsOpenPopover(null);
    setFilterUsers([]);
    setFilterShifts([]);
    setSelectedShifts([]);
    setSelectedCapacity([]);
    setFilterCapacity([]);
  }, [setFilterStartDate, setFilterEndDate, setIsOpenPopover, setFilterUsers, setFilterShifts, setFilterCapacity]);

  const handleApplyFilters = useCallback(() => {
    setFilterStartDate(startDate);
    setFilterEndDate(endDate);
    setIsOpenPopover(null);
    setFilterShifts(selectedShift);
    setFilterCapacity(selectedCapacity);
  }, [startDate, endDate, selectedShift, selectedCapacity, setFilterStartDate, setFilterEndDate, setIsOpenPopover, setFilterShifts, setFilterCapacity]);

  return (
    <div className="CalendarViewsHeader">
      {!dashboard ? (
        <>
          <div className="d-flex align-items-center gap-2">
            <CustomPopover
              width={"364px"}
              icon={
                <Button
                  btnClass="AllProjectSelect text-sm-600 subHeading-color"
                  label={project.name}
                  rightIcon={
                    <span className="ms-1 d-inline-flex">
                      <RegularArrowDownIcon />
                    </span>
                  }
                  clickHandler={() => setIsOpenPopover("projects")}
                />
              }
              status={openPopover === "projects"}
            >
              <div className="d-flex flex-column gap-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h6>Filter by projects</h6>
                  <div onClick={() => setIsOpenPopover(null)}>
                    <Cross />
                  </div>
                </div>
                <div>
                  <IconInput
                    icon={<SearchIcon />}
                    placeholder={"Search"}
                    handleChange={handleChange}
                    value={filterValue}
                  />
                </div>
                <div style={{ height: "176px", overflow: "auto" }}>
                  {projectsLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: "100%" }}>
                      <Spin size="small" />
                    </div>
                  ) : (

                    projectsList.length === 0 && !projectsLoading ?
                      (
                        <div className="h-full flex flex-col items-center justify-center p-8">
                          <FileIcon size={18} color="#342c82" className="mb-1" />
                          <h3 className="text-sm font-semibold text-gray-800 mb-2">
                            No project found
                          </h3>

                        </div>
                      ) :
                      <div className="d-flex flex-wrap gap-2">
                        {!filterValue && project.name !== "All Projects" && (
                          <span
                            className={styles.projectItem}
                            onClick={() => {
                              setProject({ name: "All Projects" });
                              setIsOpenPopover(null);
                              setFilterValue("");
                            }}
                          >
                            All Projects
                          </span>
                        )}

                        {projectsList
                          .filter((item) => item.name !== project.name)
                          .map((proj) => (
                            <span
                              key={proj.id}
                              className={styles.projectItem}
                              onClick={() => {
                                setProject({ name: proj.name, id: proj.id });
                                setIsOpenPopover(null);
                                setFilterValue("");
                              }}
                            >
                              {proj.name}
                            </span>
                          ))}


                      </div>

                  )}
                </div>
              </div>
            </CustomPopover>

            <CustomPopover
              icon={
                <div className={styles.filter}>
                  <button
                    id="cars"
                    className="FilterBtn"
                    onClick={() => setIsOpenPopover("filters")}
                  >
                    <FilterIconSvg color="#342c82"/>
                  </button>
                  {filterCount > 0 && (
                    <div className={styles.filterCounterContainer}>
                      <span className={styles.filterCounter}>
                        {filterCount}
                      </span>
                    </div>
                  )}
                </div>
              }
              PopSurfaceClass="FilterPopoverSurface"
              width={"456px"}
              height={"450px"}
              status={openPopover === "filters"}
            >
              <div className="d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center FilterPopoverHeader">
                  <h4>Filter</h4>
                  <div onClick={() => setIsOpenPopover(null)}>
                    <Cross />
                  </div>
                </div>
                <div className="FilterPopoverBody">
                  <div className="FilterPopoverBodyScroll">
                    <div className="d-flex flex-column gap-2 mb-32">
                      <h6>Shifts</h6>
                      <div className="flex row w-100 gap-2 flex-wrap">
                        {shifts.map((item) => (
                          <div key={item.id}>
                            <CheckboxInputStateSave
                              label={item.name}
                              checked={selectedShift.includes(item.id)}
                              onChange={() => handleSelectShift(item.id)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-2 mb-32">
                      <h6>Capacity</h6>
                      <div className="d-flex gap-5">
                        <div className="d-flex flex-column gap-2">
                          <CheckboxInputStateSave
                            label="Fully Available"
                            checked={selectedCapacity.includes("1")}
                            onChange={() => handleSelectCapacity("1")}
                          />
                          <CheckboxInputStateSave
                            label="Partially Available"
                            checked={selectedCapacity.includes("2")}
                            onChange={() => handleSelectCapacity("2")}
                          />
                          <CheckboxInputStateSave
                            label="Time Off"
                            checked={selectedCapacity.includes("5")}
                            onChange={() => handleSelectCapacity("5")}
                          />
                        </div>
                        <div className="d-flex flex-column gap-2">
                          <CheckboxInputStateSave
                            label="Fully Booked"
                            checked={selectedCapacity.includes("3")}
                            onChange={() => handleSelectCapacity("3")}
                          />
                          <CheckboxInputStateSave
                            label="Over Booked"
                            checked={selectedCapacity.includes("4")}
                            onChange={() => handleSelectCapacity("4")}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-2">
                      <h6>Resource working on dates</h6>
                      <div className="d-flex align-items-center justify-content-between">
                        <CustomInput
                          type="date"
                          icon={<CalendarIcon />}
                          label="Start date"
                          width={"182px"}
                          handleChange={setStartDate}
                          value={startDate}
                        />

                        <img
                          src={ArrowForward}
                          alt="Next Arrow"
                          className="mt-4"
                        />

                        <CustomInput
                          type="date"
                          icon={<CalendarIcon />}
                          label="End date"
                          width={"182px"}
                          handleChange={setEndDate}
                          value={endDate}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-end gap-2 justify-content-end FilterPopoverFooter">
                  <Button
                    btnClass="d_btn d_btn_white d_btn_whiteMd"
                    label="Clear All"
                    clickHandler={handleClearFilters}
                  />
                  <Button
                    btnClass="d_btn d_btn_primary d_btn_primaryMd"
                    label="Apply"
                    clickHandler={handleApplyFilters}
                  />
                </div>
              </div>
            </CustomPopover>

            <div className={filterStartDate && styles.dateRange}>
              {filterStartDate && <CalendarIcon />}
              {filterStartDate && (
                <span
                  className={styles.cross}
                  onClick={() => {
                    setFilterStartDate(null);
                    setFilterEndDate(null);
                    setStartDate(null);
                    setEndDate(null);
                  }}
                >
                  <Cross />
                </span>
              )}
            </div>
          </div>

          <div className="d-flex align-item-center gap-2">
            <Button
              btnClass="todayBtn text-sm-600 subHeading-color"
              label="Today"
              name="today"
              id="cars"
              clickHandler={handleTodayClick}
            />

            {!filterStartDate && currentPeriod.displayText && (
              <div className={styles.periodNavigation}>
                <button
                  className={styles.navButton}
                  onClick={goToPreviousPeriod}
                  title="Previous"
                >
                  <ArrowLeft />
                </button>
                <span className={styles.periodText}>
                  {currentPeriod.displayText}
                </span>
                <button
                  className={styles.navButton}
                  onClick={goToNextPeriod}
                  title="Next"
                >
                  <ArrowRight />
                </button>
              </div>
            )}

            <div className="d-flex align-item-center gap-1">
              {!filterStartDate && (
                <button
                  className={
                    buttonStatus === "month"
                      ? "calendarViewBtnSelect"
                      : "calendarViewBtn"
                  }
                  onClick={() => {
                    handleCalendarsView("Month");
                    setIsOpenPopover(null);
                  }}
                >
                  <span className="ViewIcon">
                    <MonthIcon />
                  </span>
                  <span className="ViewActiveIcon">
                    <MonthActiveIcon />
                  </span>
                  Month
                </button>
              )}
              {!filterStartDate && (
                <button
                  className={
                    buttonStatus === "week"
                      ? "calendarViewBtnSelect"
                      : "calendarViewBtn"
                  }
                  onClick={() => {
                    handleCalendarsView("Week");
                    setIsOpenPopover(null);
                  }}
                >
                  <span className="ViewIcon">
                    <WeekIcon />
                  </span>
                  <span className="ViewActiveIcon">
                    <WeekActiveIcon />
                  </span>
                  Week
                </button>
              )}
              <button
                className={
                  buttonStatus === "day"
                    ? "calendarViewBtnSelect"
                    : "calendarViewBtn"
                }
                onClick={() => {
                  handleCalendarsView("Day");
                  setIsOpenPopover(null);
                }}
              >
                <span className="ViewIcon">
                  <DayIcon />
                </span>
                <span className="ViewActiveIcon">
                  <DayActiveIcon />
                </span>
                Day
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <h5>Resource monitoring</h5>
          <div className="d-flex align-items-center gap-12">
            <CustomPopover
              icon={
                <Button
                  icon={selection.icon}
                  label={selection.name}
                  btnClass={`${styles.calendarBtn} d_btn`}
                  rightIcon={
                    <span className="ms-6 d-inline-flex">
                      <ArrowDownMedium />
                    </span>
                  }
                  clickHandler={() =>
                    setIsOpenPopover(openPopover === "range" ? null : "range")
                  }
                />
              }
              width="96px"
              PopSurfaceClass={styles.calendarPopover}
              status={openPopover === "range"}
            >
              <div
                className={styles.calendarPopoverItem}
                onClick={() => {
                  setSelection({ icon: <MonthIcon />, name: "Month" });
                  setIsOpenPopover(null);
                  handleCalendarsView("Month");
                }}
              >
                <MonthIcon />
                Month
              </div>
              <div
                className={styles.calendarPopoverItem}
                onClick={() => {
                  setSelection({ icon: <WeekIcon />, name: "Week" });
                  setIsOpenPopover(null);
                  handleCalendarsView("Week");
                }}
              >
                <WeekIcon />
                Week
              </div>
              <div
                className={styles.calendarPopoverItem}
                onClick={() => {
                  setSelection({ icon: <DayIcon />, name: "Day" });
                  setIsOpenPopover(null);
                  handleCalendarsView("Day");
                }}
              >
                <DayIcon />
                Day
              </div>
            </CustomPopover>
            <Button
              clickHandler={() => {
                clickHandler("resources");
                setResource(!resource);
              }}
              icon={
                <img
                  src={
                    resource
                      ? `${process.env.PUBLIC_URL}/ArrowExpand.svg`
                      : `${process.env.PUBLIC_URL}/ArrowContract.svg`
                  }
                  alt="arrow"
                />
              }
              btnClass={styles.ArrowExpandStyles}
            />
          </div>
        </>
      )}
    </div>
  );
});

ResourceHeader.displayName = 'ResourceHeader';

const mapDispatchToProps = {
  getProjectsRequest: getProjects,
};

const mapStateToProps = (state) => {
  const allProjects = projects(state);
  return { allProjects };
};

export default connect(mapStateToProps, mapDispatchToProps)(ResourceHeader);